import { Button } from "@/components/ui/button";
import { useState } from "react";
import { NeighborhoodFilter } from "./filters/NeighborhoodFilter";
import { EventTypeFilter } from "./filters/EventTypeFilter";
import { GroupSizeFilter } from "./filters/GroupSizeFilter";
import { PriceRangeFilter } from "./filters/PriceRangeFilter";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { SlidersHorizontal, X } from "lucide-react";
import type { FilterParams, FilterChangeHandler } from "@/types/filters";
import { RestaurantSearch } from "./RestaurantSearch";

export const EventFilters = ({ onFilterChange, onClearFilters }: FilterChangeHandler) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [filters, setFilters] = useState<FilterParams>({
    neighborhood: '',
    eventType: '',
    groupSize: '',
    priceRange: '',
    search: ''
  });

  const handleFilterChange = (type: keyof FilterParams, value: string) => {
    console.log('EventFilters - Filter changed:', { type, value });
    const newFilters = { ...filters, [type]: value };
    setFilters(newFilters);
    onFilterChange(type, value);
  };

  const handleClearFilters = () => {
    console.log('EventFilters - Clearing all filters');
    setFilters({
      neighborhood: '',
      eventType: '',
      groupSize: '',
      priceRange: '',
      search: ''
    });
    onClearFilters();
    
    toast({
      title: "Filters Cleared",
      description: "All filters have been reset",
      duration: 2000,
    });
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="w-full space-y-3">
      {isMobile && (
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between rounded-full h-10 border-border/60"
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="text-sm">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-secondary text-secondary-foreground text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {activeFiltersCount}
              </span>
            )}
          </span>
          {isExpanded ? <X className="h-3.5 w-3.5" /> : null}
        </Button>
      )}
      
      {(isExpanded || !isMobile) && (
        <div className={`flex flex-wrap items-center gap-2.5 ${isMobile ? 'flex-col w-full animate-fade-in' : ''}`}>
          <div className={`flex flex-wrap gap-2.5 ${isMobile ? 'w-full flex-col' : ''}`}>
            <NeighborhoodFilter
              value={filters.neighborhood}
              onChange={(value) => handleFilterChange("neighborhood", value)}
            />
            <EventTypeFilter
              value={filters.eventType}
              onChange={(value) => handleFilterChange("eventType", value)}
            />
            <GroupSizeFilter
              value={filters.groupSize}
              onChange={(value) => handleFilterChange("groupSize", value)}
            />
            <PriceRangeFilter
              value={filters.priceRange}
              onChange={(value) => handleFilterChange("priceRange", value)}
            />
          </div>
          
          {isMobile && (
            <div className="w-full mt-2 border-t pt-3 border-border/40">
              <RestaurantSearch onSearch={(value) => handleFilterChange("search", value)} />
            </div>
          )}
          
          {activeFiltersCount > 0 && (
            <Button 
              variant="ghost"
              onClick={handleClearFilters}
              size="sm"
              className={`text-muted-foreground hover:text-foreground text-xs ${isMobile ? 'w-full mt-2' : ''}`}
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
